import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Typography,
    Box,
    Divider,
    Button,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material'
import {
    Notifications,
    NotificationsNone,
    MarkEmailRead,
    Assignment,
    Comment,
    NavigateNext,
    CheckCircle
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useNotifications } from '@hooks/useNotifications'
import { formatRelativeTime } from '@utils/helpers'

const NotificationBell = () => {
    const { t, language } = useTranslation()
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [newNotification, setNewNotification] = useState(null)
    const prevUnreadCountRef = useRef(0)

    const {
        notifications,
        unreadCount,
        isLoading,
        markAsRead,
        markAllAsRead,
        isMarkingAllRead
    } = useNotifications()

    // Detect new notifications and show popup
    useEffect(() => {
        console.log('Notification popup check - unreadCount:', unreadCount, 'prevCount:', prevUnreadCountRef.current, 'notifications:', notifications.length)

        if (unreadCount > prevUnreadCountRef.current && notifications.length > 0) {
            // Find the newest unread notification (is_read can be 0, false, or "0")
            const newestUnread = notifications.find(n => !n.is_read || n.is_read === 0 || n.is_read === '0')
            console.log('Newest unread notification:', newestUnread)
            if (newestUnread) {
                setNewNotification(newestUnread)
                setSnackbarOpen(true)
            }
        }
        prevUnreadCountRef.current = unreadCount
    }, [unreadCount, notifications])

    const handleSnackbarClose = () => {
        setSnackbarOpen(false)
    }

    const handleSnackbarClick = () => {
        if (newNotification?.submission_id) {
            navigate(`/submissions/${newNotification.submission_id}`)
        }
        setSnackbarOpen(false)
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleNotificationClick = (notification) => {
        // Mark as read
        if (!notification.is_read) {
            markAsRead(notification.id)
        }

        // Navigate to submission if available
        if (notification.submission_id) {
            navigate(`/submissions/${notification.submission_id}`)
        }

        handleClose()
    }

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'new_submission':
                return <Assignment color="primary" />
            case 'navigated_submission':
                return <NavigateNext sx={{ color: '#1a3a6b' }} />
            case 'department_comment':
                return <Comment color="secondary" />
            default:
                return <Notifications />
        }
    }

    const getNotificationTitle = (notification) => {
        return language === 'ar' ? notification.title_ar : notification.title_en
    }

    const getNotificationMessage = (notification) => {
        return language === 'ar' ? notification.message_ar : notification.message_en
    }

    return (
        <>
            <Tooltip title={language === 'ar' ? 'الإشعارات' : 'Notifications'}>
                <IconButton
                    onClick={handleClick}
                    size="large"
                    sx={{ color: 'inherit' }}
                >
                    <Badge
                        badgeContent={unreadCount}
                        color="error"
                        max={99}
                    >
                        {unreadCount > 0 ? <Notifications /> : <NotificationsNone />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: 360,
                        maxHeight: 480,
                        overflow: 'auto'
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {language === 'ar' ? 'الإشعارات' : 'Notifications'}
                    </Typography>
                    {unreadCount > 0 && (
                        <Button
                            size="small"
                            onClick={() => markAllAsRead()}
                            disabled={isMarkingAllRead}
                            startIcon={isMarkingAllRead ? <CircularProgress size={14} /> : <MarkEmailRead />}
                        >
                            {language === 'ar' ? 'قراءة الكل' : 'Mark all read'}
                        </Button>
                    )}
                </Box>
                <Divider />

                {/* Loading state */}
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {/* Empty state */}
                {!isLoading && notifications.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        <CheckCircle sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                        <Typography>
                            {language === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
                        </Typography>
                    </Box>
                )}

                {/* Notification list */}
                {notifications.map((notification) => (
                    <MenuItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                            py: 1.5,
                            px: 2,
                            alignItems: 'flex-start',
                            bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                            borderLeft: notification.is_read ? 'none' : '3px solid',
                            borderColor: 'primary.main'
                        }}
                    >
                        <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                            {getNotificationIcon(notification.type)}
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: notification.is_read ? 400 : 600 }}>
                                    {getNotificationTitle(notification)}
                                </Typography>
                            }
                            secondary={
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {getNotificationMessage(notification)}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                                        {formatRelativeTime(notification.created_at)}
                                    </Typography>
                                </Box>
                            }
                        />
                    </MenuItem>
                ))}
            </Menu>

            {/* New notification popup */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 6 }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="info"
                    sx={{
                        width: '100%',
                        cursor: 'pointer',
                        bgcolor: 'primary.main',
                        color: 'white',
                        '& .MuiAlert-icon': { color: 'white' }
                    }}
                    onClick={handleSnackbarClick}
                >
                    <Typography variant="subtitle2" fontWeight={600}>
                        {newNotification ? (language === 'ar' ? newNotification.title_ar : newNotification.title_en) : ''}
                    </Typography>
                    <Typography variant="body2">
                        {newNotification ? (language === 'ar' ? newNotification.message_ar : newNotification.message_en) : ''}
                    </Typography>
                </Alert>
            </Snackbar>
        </>
    )
}

export default NotificationBell
